import { autoinject, transient } from "aurelia-dependency-injection";
import { bindable, computedFrom } from "aurelia-framework";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { Reflection } from "../../../utilities/reflection";
import { I18N } from "aurelia-i18n";
import { NotificationClientService, NotificationType } from "../../../services/basics/notification-client.service";
import { QueriesClientService } from "../../../services/queries/queries-client.services";
import { Guid } from "../../../utilities/guid";
import { ProjectionPropertyOrdering, SortProperty } from "../../../models/queries/queries.models";

@autoinject
@transient()
export class Pager {
    
    private _numberOfPagesToDisplay: number = 5;

    @bindable
    public filter: any = {};

    @bindable
    public sorts: SortProperty[];

    @bindable
    public take: number = 10;

    @bindable
    public pageIndex: number = 0;

    @bindable
    public count: number;

    @bindable
    public pageCountSet: number[] = [10, 50, 100];

    @bindable
    public url: string;

    @bindable
    public odataurl: string;

    @computedFrom(Reflection.getPropertyPath<Pager>(z => z.count), Reflection.getPropertyPath<Pager>(z => z.pageIndex), 
        Reflection.getPropertyPath<Pager>(z => z.take))
    get displayingItemsText() {
            if (this.count > 0) {
                const firstItemIndexOnPage = ((this.pageIndex) * this.take) + 1;
                let lastItemIndexOnPage = firstItemIndexOnPage + this.take - 1;
                if (lastItemIndexOnPage > this.count){
                    lastItemIndexOnPage = this.count;
                }
                return this.i18N.tr('displaying-items-text').replace("{0}", firstItemIndexOnPage.toString()).replace("{1}", lastItemIndexOnPage.toString()).replace("{2}", this.count.toString()).replace("{3}", "Items");
            }
            else {
                return "";
            }
        }

    @computedFrom(Reflection.getPropertyPath<Pager>(x => x.pageIndex), Reflection.getPropertyPath<Pager>(x => x.count), Reflection.getPropertyPath<Pager>(x => x.take))
    public get pages(): number {
        return Math.ceil(this.count / this.take)
    }

    @computedFrom(Reflection.getPropertyPath<Pager>(x => x.pageIndex), Reflection.getPropertyPath<Pager>(x => x.count), Reflection.getPropertyPath<Pager>(x => x.take))
    public get pagesToDisplay(): number[] {
        const result: number[] = [];

        if (this.pages > 1){
            for (let i = this.firstPage(); i < this._numberOfPagesToDisplay + this.firstPage(); i++){
                if (i < this.pages){
                    result.push(i);
                }
            }
        }

        return result;
    }

    @computedFrom(Reflection.getPropertyPath<Pager>(x => x.pageIndex))
    public get enablePrevious(): boolean {
        return this.pageIndex != 0;
    }

    @computedFrom(Reflection.getPropertyPath<Pager>(x => x.count), Reflection.getPropertyPath<Pager>(x => x.pageIndex), Reflection.getPropertyPath<Pager>(x => x.take))
    public get enableNext(): boolean {
        if (this.pageIndex == 0 && this.count <= this.take){
            return false;
        }
        return this.count > (this.take*(this.pageIndex+1));
    }

    constructor(private requestClient: RequestClientService,
        private i18N: I18N,
        private element: Element,
        private notificationService: NotificationClientService,
        private queryService: QueriesClientService) {
    }

    public changePage(currentPageNumber){
        this.pageIndex = currentPageNumber;
        this.triggerCallback();
    }

    public firstPage(): number {
        let result: number = 0;

        if (this.pageIndex <=2 || this.pages <= this._numberOfPagesToDisplay){
            return result;
        }
        else if (this.pageIndex >= 2 && this.pageIndex+2 < this.pages ){
            result = this.pageIndex - 2;
        }
        else if (this.pageIndex+2 >= this.pages){
            result = this.pageIndex - this._numberOfPagesToDisplay + (this.pages - this.pageIndex)
        }

        return result;
    }

    public setTake(pageCount){
        this.take = pageCount;
        this.pageIndex = 0;
        this.triggerCallback();
    }

    public getPageNumber(){
        return Math.round(this.count/this.take);
    }

    filterChanged(){
        this.triggerCallback();
    }

    public triggerOdataCallback() {
        const skip = this.pageIndex * this.take;
        let url = `${this.odataurl}?$skip=${skip}&$top=${this.take}&$count=true`;

        if (this.filter){
            for (const property in this.filter) {
                if (this.filter[property] !== undefined && this.filter[property] !== null){
                    url += `&filter.${property}=${this.filter[property]}`;
                }
            }
        }

        if (this.sorts) {
            [...this.sorts].forEach((sort: SortProperty) => {
                url += `&$orderby=${sort.propertyName} ${sort.order === ProjectionPropertyOrdering.Ascending ? 'asc' : 'desc'}`; 
            })
        }

        this.queryService.getData(url, Guid.generateGuid())
        .then((result: any) => {
            const customEvent: CustomEvent = new CustomEvent('data-retrieved', { bubbles: true, detail: result });
            this.element.dispatchEvent(customEvent);
        }, () => {
            this.notificationService.show('Failed to load data', '', NotificationType.Error);
        });
    }

    public triggerCallback(){
        if (this.url) {
            const skip = this.pageIndex * this.take;
            let url = `${this.url}?skip=${skip}&take=${this.take}`;
    
            if (this.filter){
                for (const property in this.filter) {
                    if (this.filter[property] !== undefined && this.filter[property] !== null){
                        url += `&filter.${property}=${this.filter[property]}`;
                    }
                }
            }
    
            this.requestClient.get<any>(url, null)
            .then((result: any) => {
                const customEvent: CustomEvent = new CustomEvent('data-retrieved', { bubbles: true, detail: result });
                this.element.dispatchEvent(customEvent);
            }, () => {
                this.notificationService.show('Failed to load data', '', NotificationType.Error);
            });
        } else if (this.odataurl) {
            this.triggerOdataCallback();
        }
    }

     nextClick(): void {
        if (this.enableNext) {
            this.pageIndex++;
            this.triggerCallback();
        }
    }

    previousClick(): void {
        if (this.enablePrevious) {
            this.pageIndex--;
            this.triggerCallback();
        }
    }
}